<template>
    <div class="animated fadeIn h-100 admin-container">
        <div class="page-content">
            <div class="d-flex float-right align-items-center mb-2">
                <div class="input-group border-bottom position-relative" style="width: 190px">
                    <input class="form-control bg-transparent border-none pr-4" :placeholder="$t('common.search')"
                        aria-label="Search" v-model="searchKeyword" style="min-width: 190px" />
                    <i class="fa fa-search position-absolute" style="top: 50%; right: 4px; margin-top: -6px" />
                </div>
                <div class="mx-3 font-20">|</div>
                <div class="d-flex align-items-center text-nowrap">
                    <b-button variant="outline-success" class="mr-2" @click="handleExportExcel" :disabled="isExporting">
                        <i class="fa fa-download mr-2"></i>
                        <span class="text-uppercase">Xuất dữ liệu</span>
                    </b-button>
                    <b-button variant="outline-info" class="mr-2" @click="triggerImportExcel" :disabled="isImporting">
                        <i class="fa fa-upload mr-2"></i>
                        <span class="text-uppercase">Nhập dữ liệu</span>
                    </b-button>
                    <b-button variant="outline-primary" @click="openCreateModal">
                        <i class="fa fa-plus-circle mr-2"></i>
                        <span class="text-uppercase">Tạo mới</span>
                    </b-button>
                    <input ref="importExcelInput" type="file" accept=".xlsx,.xls" class="d-none"
                        @change="handleImportExcel" />
                </div>
            </div>
            <b-table class="mt-2" striped hover :items="listPoint" :fields="fields" show-empty :filter="searchKeyword"
                :filter-included-fields="['name']" :current-page="currentPage" :per-page="perPage"
                @filtered="onFiltered" :busy="isTableLoading">
                <template v-slot:cell(index)="data">{{ (currentPage - 1) * perPage + (data.index + 1) }}</template>
                <template v-slot:cell(layerId)="data">{{ data.item.layer?.name }}</template>
                <template v-slot:cell(chartType)="data">{{
                    data.item.chartType == 'line' ? 'Đường' : 'Cột'
                }}</template>
                <template v-slot:cell(createdAt)="data">
                    <span>{{ data.value | formatDateTime }}</span>
                </template>
                <template v-slot:cell(actions)="row">
                    <b-button variant="primary" class="mr-1 min-width-none" @click="openEditModal(row.item)"
                        v-b-tooltip.hover :title="$t('common.edit')">
                        <i class="fa fa-pencil"></i>
                    </b-button>
                    <b-button variant="success" class="mr-1 min-width-none" @click="openDataModal(row.item)"
                        v-b-tooltip.hover title="Xem dữ liệu">
                        <i class="fa fa-eye"></i>
                    </b-button>
                    <b-button variant="warning" class="mr-1 min-width-none" @click="openChartModal(row.item)"
                        v-b-tooltip.hover title="Xem biểu đồ">
                        <i class="fa fa-bar-chart"></i>
                    </b-button>
                    <b-button variant="danger" @click="confirmDelete(row.item)" v-b-tooltip.hover class="min-width-none"
                        :title="$t('common.delete')">
                        <i class="fa fa-trash"></i>
                    </b-button>
                </template>
                <template v-slot:empty>
                    <div class="text-center">
                        <span>Không có dữ liệu</span>
                    </div>
                </template>
                <template v-slot:table-busy>
                    <div class="text-center text-primary my-3">
                        <b-spinner small class="align-middle mr-2"></b-spinner>
                        <strong>Đang tải dữ liệu...</strong>
                    </div>
                </template>
            </b-table>
            <div v-if="totalRows > perPage" class="my-1">
                <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage"
                    :hide-goto-end-buttons="true" :prev-text="$t('common.prevPage')" :next-text="$t('common.nextPage')"
                    class="my-0" align="center" />
            </div>
            <b-modal ref="createOrUpdateModal" :title="form.id ? `Cập nhật (ID: ${form.id})` : $t('common.create')"
                ok-title="Lưu" size="lg" @ok="handleCreateOk" :cancel-title="$t('common.cancel')">
                <b-form-group label="Tên">
                    <b-form-input v-model="form.name" v-validate="'required'" placeholder="Tên" name="name"
                        data-vv-as="Tên"></b-form-input>
                    <div v-show="errors.has('name')" class="validation-message text-danger">
                        {{ errors.first('name') }}
                    </div>
                </b-form-group>
                <b-form-group label="Vĩ độ">
                    <b-form-input v-model="form.lat" v-validate="'required'" placeholder="Vĩ độ" name="lat"
                        type="number" data-vv-as="Vĩ độ"></b-form-input>
                    <div v-show="errors.has('lat')" class="validation-message text-danger">
                        {{ errors.first('lat') }}
                    </div>
                </b-form-group>
                <b-form-group label="Kinh độ">
                    <b-form-input v-model="form.long" v-validate="'required'" placeholder="Kinh độ" type="number"
                        name="long" data-vv-as="Kinh độ"></b-form-input>
                    <div v-show="errors.has('long')" class="validation-message text-danger">
                        {{ errors.first('long') }}
                    </div>
                </b-form-group>
                <b-form-group label="Loại biểu đồ">
                    <b-form-radio-group v-model="form.chartType">
                        <b-form-radio value="line">Đường</b-form-radio>
                        <b-form-radio value="bar">Cột</b-form-radio>
                    </b-form-radio-group>
                </b-form-group>
                <b-form-group label="Mã trạm (nếu có)">
                    <b-form-input v-model="form.code" placeholder="Mã trạm" name="code" type="text"></b-form-input>
                </b-form-group>
                <b-form-group label="Liên kết tới lớp dữ liệu">
                    <treeselect :searchable="false" :options="listLayerMap" :normalizer="normalizer"
                        v-model="form.layerId" placeholder="Chọn lớp dữ liệu" />
                </b-form-group>
                <b class="mb-1">Phân cấp màu:</b>
                <b-row class="mb-1">
                    <b-col cols="3">
                        <label>Tên</label>
                    </b-col>
                    <b-col cols="3">
                        <label>Màu sắc</label>
                    </b-col>
                    <b-col cols="3">
                        <label>Icon</label>
                    </b-col>
                    <b-col cols="3">
                        <label>Giá trị giới hạn</label>
                    </b-col>
                </b-row>
                <b-row v-for="(item, index) in legend" :key="index" class="mb-2">
                    <b-col cols="3">
                        <b-form-input v-model="item.name"></b-form-input>
                    </b-col>
                    <b-col cols="3">
                        <b-form-input v-model="item.color" type="color"></b-form-input>
                    </b-col>
                    <b-col cols="3">
                        <div class="legend-icon-compact">
                            <b-form-file v-model="item.iconFile" accept="image/*" placeholder="Chọn" browse-text="Chọn"
                                @input="onLegendIconSelected(item)" class="legend-icon-input"></b-form-file>
                            <b-img v-if="getLegendIconPreview(item)" fluid class="legend-icon-preview"
                                :src="getLegendIconPreview(item)" />
                            <b-button v-if="getLegendIconPreview(item)" size="sm" variant="outline-danger"
                                class="legend-icon-remove" @click="clearLegendIcon(item)">
                                <i class="fa fa-trash"></i>
                            </b-button>
                        </div>
                    </b-col>
                    <b-col cols="3">
                        <b-row no-gutters>
                            <b-col cols="8">
                                <b-form-input v-model="item.value" type="number"></b-form-input>
                            </b-col>
                            <b-col cols="4" class="pl-2">
                                <b-button variant="danger" @click="removeLegendItem(item)">Xoá</b-button>
                            </b-col>
                        </b-row>
                    </b-col>
                </b-row>
                <b-button variant="primary"
                    @click="legend.push({ color: '#000', icon: null, iconFile: null, iconPreview: null })">Thêm cấp
                    màu</b-button>
            </b-modal>
            <b-modal ref="dataModal" size="xl" hide-footer>
                <template #modal-title>
                    <div class="d-flex align-items-center">
                        <span>Dữ liệu</span>
                        <b-button size="sm" variant="outline-info" class="ml-2 px-2 py-0" v-b-tooltip.hover
                            title="Hướng dẫn đẩy dữ liệu lên bằng API" @click.stop="openApiGuideModal">?</b-button>
                    </div>
                </template>
                <DataPoint :pointId="selectedPoint?.id" />
            </b-modal>
            <b-modal ref="apiGuideModal" title="Hướng dẫn đẩy dữ liệu lên bằng API" size="lg" hide-footer>
                <div class="mb-2 d-flex justify-content-between align-items-center">
                    <span>Ví dụ Python:</span>
                    <b-button size="sm" variant="outline-primary" @click="copyApiGuideCode" v-b-tooltip.hover
                        title="Copy code">
                        <i class="fa fa-copy"></i>
                    </b-button>
                </div>
                <pre class="api-guide-code mb-0"><code>{{ getApiGuideCode() }}</code></pre>
                <div class="mt-3">
                    <div class="font-weight-bold mb-2">Giải thích tham số:</div>
                    <ul class="api-guide-list mb-0 pl-3">
                        <li><code>payload</code>: mảng JSON, mỗi phần tử là một bản ghi dữ liệu.</li>
                        <li><code>time</code>: thời gian bản ghi theo chuẩn ISO 8601 (UTC), ví dụ
                            <code>2026-03-25T17:00:00.000Z</code>.
                        </li>
                        <li><code>type</code>: tên loại dữ liệu, ví dụ <code>Mực nước (m)</code>.</li>
                        <li><code>value</code>: giá trị đo tương ứng với <code>type</code> (số nguyên hoặc số thực).
                        </li>
                        <li><code>pointId</code>: ID điểm cần đẩy dữ liệu vào.</li>
                    </ul>
                </div>
            </b-modal>
            <b-modal hide-header hide-footer centered size="xl" modal-class="station-modal" ref="chartModal">
                <Statistic :point="selectedPoint" />
            </b-modal>
        </div>
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import DataPoint from '@/components/DataPoint';
import Statistic from '@/components/Statistic';
import API_CONFIG from '@/api-config';
import Treeselect from '@riophae/vue-treeselect';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';

export default {
    components: {
        DatePicker,
        DataPoint,
        Treeselect,
        Statistic
    },
    async mounted() {
        await this.$nextTick();
        this.listDataType = await this.getDataType();
        await this.refreshPoints();
        this.listLayerMap = await this.getLayerTree({ type: 'layer' });
    },
    beforeDestroy() {
        this.clearLegendPreviewUrls();
    },
    data: function () {
        return {
            fields: [
                { tdClass: 'align-middle', key: 'index', label: '#' },
                {
                    tdClass: 'align-middle',
                    thClass: 'fixed-actions-col',
                    key: 'name',
                    label: 'Tên'
                },
                {
                    tdClass: 'align-middle',
                    thClass: 'fixed-actions-col',
                    key: 'lat',
                    label: 'Vĩ độ'
                },
                {
                    tdClass: 'align-middle',
                    thClass: 'fixed-actions-col',
                    key: 'long',
                    label: 'Kinh độ'
                },
                {
                    tdClass: 'align-middle',
                    thClass: 'fixed-actions-col',
                    key: 'chartType',
                    label: 'Loại biểu đồ'
                },
                {
                    tdClass: 'align-middle',
                    thClass: 'fixed-actions-col',
                    key: 'layerId',
                    label: 'Lớp dữ liệu liên kết'
                },
                {
                    thClass: 'fixed-actions-col',
                    key: 'actions',
                    label: this.$i18n.t('common.actions'),
                    thStyle: { width: '200px' }
                }
            ],
            form: {},
            searchKeyword: null,
            listPoint: [],
            selectedPoint: null,
            listData: [],
            file: null,
            listLayerMap: [],
            legend: [],
            listDataType: [],
            isExporting: false,
            isImporting: false,
            isTableLoading: false,
            currentPage: 1,
            perPage: 20,
            totalRows: 0
        };
    },
    methods: {
        ...mapActions({
            getList: 'point/getList',
            create: 'point/create',
            delete: 'point/delete',
            update: 'point/update',
            exportExcel: 'point/exportExcel',
            importExcel: 'point/importExcel',
            getLayerTree: 'mapLayer/getListTree',
            getDataType: 'data/getDataType',
            upload: 'file/upload',
            createAuditByUser: 'user/createAuditByUser'
        }),
        async refreshPoints() {
            this.isTableLoading = true;
            try {
                this.listPoint = await this.getList();
                this.totalRows = this.listPoint.length;
                const totalPages = Math.max(Math.ceil(this.totalRows / this.perPage), 1);
                if (this.currentPage > totalPages) {
                    this.currentPage = totalPages;
                }
            } finally {
                this.isTableLoading = false;
            }
        },
        onFiltered(filteredItems) {
            this.totalRows = filteredItems.length;
            this.currentPage = 1;
        },
        async handleExportExcel() {
            this.isExporting = true;
            try {
                const response = await this.exportExcel();
                const disposition = response?.headers?.['content-disposition'];
                const fileName =
                    this.getFileNameFromDisposition(disposition) ||
                    `point-${new Date().toISOString().slice(0, 10)}.xlsx`;
                const blob = response?.data instanceof Blob ? response.data : new Blob([response?.data]);
                const fileUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = fileUrl;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(fileUrl);
                this.$toaster.success('Xuất dữ liệu thành công');
                this.createAuditByUser({ content: 'Xuất điểm ra Excel' });
            } finally {
                this.isExporting = false;
            }
        },
        triggerImportExcel() {
            if (this.$refs.importExcelInput) {
                this.$refs.importExcelInput.value = '';
                this.$refs.importExcelInput.click();
            }
        },
        async handleImportExcel(event) {
            const [file] = event.target.files || [];
            if (!file) return;
            this.isImporting = true;
            try {
                const response = await this.importExcel({ formData: { file } });
                const created = response?.created ?? 0;
                const updated = response?.updated ?? 0;
                const skipped = response?.skipped ?? 0;
                const total = response?.total ?? created + updated + skipped;
                this.$toaster.success(
                    `Nhập dữ liệu thành công (Mới: ${created}, Cập nhật: ${updated}, Bỏ qua: ${skipped}, Tổng: ${total})`
                );
                this.createAuditByUser({ content: 'Nhập điểm từ Excel' });
                await this.refreshPoints();
            } finally {
                this.isImporting = false;
                event.target.value = '';
            }
        },
        getFileNameFromDisposition(disposition) {
            if (!disposition) return '';
            const utf8Match = disposition.match(/filename\*=UTF-8''([^;\n]+)/i);
            if (utf8Match?.[1]) {
                return decodeURIComponent(utf8Match[1]).replace(/"/g, '');
            }
            const asciiMatch = disposition.match(/filename=([^;\n]+)/i);
            if (!asciiMatch?.[1]) return '';
            return asciiMatch[1].trim().replace(/"/g, '');
        },
        handleCreateOk(modal) {
            modal.preventDefault();
            this.$validator.validateAll().then(result => {
                if (result) {
                    this.submitForm();
                }
            });
        },
        async openEditModal(item) {
            this.clearLegendPreviewUrls();
            this.resetForm();
            this.form = { ...item };
            if ((item.legend ?? []).length > 0) {
                this.legend = item.legend.map(legendItem => ({
                    ...legendItem,
                    iconFile: null,
                    iconPreview: null
                }));
            }
            this.$refs.createOrUpdateModal.show();
        },
        async openDataModal(item) {
            this.selectedPoint = item;
            this.$refs.dataModal.show();
        },
        openApiGuideModal() {
            this.$refs.apiGuideModal.show();
        },
        async openChartModal(item) {
            this.selectedPoint = item;
            this.$refs.chartModal.show();
        },
        openCreateModal() {
            this.resetForm();
            this.$refs.createOrUpdateModal.show();
        },
        resetForm() {
            this.clearLegendPreviewUrls();
            this.form = { chartType: 'line' };
            this.file = null;
            this.legend = [
                { color: '#ff0000', icon: null, iconFile: null, iconPreview: null },
                { color: '#ffa500', icon: null, iconFile: null, iconPreview: null },
                { color: '#008000', icon: null, iconFile: null, iconPreview: null }
            ];
        },
        async confirmDelete(item) {
            const result = await this.$bvModal.msgBoxConfirm('Bạn có chắc chắn muốn xoá không?', {
                title: 'Xác nhận',
                okVariant: 'danger',
                okTitle: 'Có',
                cancelTitle: 'Không'
            });
            if (result) {
                await this.delete(item);
                await this.refreshPoints();
                this.$toaster.success('Xoá thành công');
                this.createAuditByUser({ content: 'Xoá điểm' });
            }
        },
        async submitForm() {
            const legend = [];
            for (const item of this.legend) {
                const legendItem = { ...item };
                if (legendItem.iconFile) {
                    const data = await this.upload({ formData: { file: legendItem.iconFile } });
                    legendItem.icon = data.path;
                }
                delete legendItem.iconFile;
                delete legendItem.iconPreview;
                legend.push(legendItem);
            }
            this.form.legend = legend;
            if (this.form.id) {
                await this.update({ ...this.form, layerId: this.form.layerId || null });
            } else {
                await this.create(this.form);
            }
            this.$toaster.success('Thành công');
            this.createAuditByUser({ content: 'Cập nhật điểm' });
            await this.refreshPoints();
            this.$refs.createOrUpdateModal.hide();
        },
        normalizer(node) {
            return {
                id: node.id,
                label: node.name,
                children: node.children
            };
        },
        getLegendIconUrl(path) {
            if (!path) return '';
            if (path.startsWith('http://') || path.startsWith('https://')) return path;
            return API_CONFIG.BASE_URL + 'download?fileName=' + path;
        },
        getLegendIconPreview(item) {
            if (item?.iconPreview) return item.iconPreview;
            if (item?.icon) return this.getLegendIconUrl(item.icon);
            return '';
        },
        onLegendIconSelected(item) {
            if (item.iconPreview) {
                URL.revokeObjectURL(item.iconPreview);
                item.iconPreview = null;
            }
            if (item.iconFile instanceof File) {
                item.iconPreview = URL.createObjectURL(item.iconFile);
            }
        },
        clearLegendPreviewUrls() {
            for (const item of this.legend) {
                if (item?.iconPreview) {
                    URL.revokeObjectURL(item.iconPreview);
                    item.iconPreview = null;
                }
            }
        },
        clearLegendIcon(item) {
            if (item.iconPreview) {
                URL.revokeObjectURL(item.iconPreview);
            }
            item.icon = null;
            item.iconFile = null;
            item.iconPreview = null;
        },
        removeLegendItem(item) {
            if (item?.iconPreview) {
                URL.revokeObjectURL(item.iconPreview);
            }
            this.legend.splice(this.legend.indexOf(item), 1);
        },
        getApiGuideCode() {
            const pointId = this.selectedPoint?.id;
            return `import requests
import json

url = "https://giamsatdubaotainguyennuoc.wri.vn/api/data"

payload = json.dumps([
  {
    "time": "2026-03-25T17:00:00.000Z",
    "type": "Mực nước (m)",
    "value": 2,
    "pointId": ${pointId}
  }
])
headers = {
  'key': 'wETb4cnl2UgI9XRy',
  'Content-Type': 'application/json'
}

response = requests.request("POST", url, headers=headers, data=payload)

print(response.text)`;
        },
        async copyApiGuideCode() {
            const code = this.getApiGuideCode();
            try {
                if (navigator.clipboard?.writeText) {
                    await navigator.clipboard.writeText(code);
                } else {
                    const textarea = document.createElement('textarea');
                    textarea.value = code;
                    textarea.setAttribute('readonly', '');
                    textarea.style.position = 'absolute';
                    textarea.style.left = '-9999px';
                    document.body.appendChild(textarea);
                    textarea.select();
                    document.execCommand('copy');
                    document.body.removeChild(textarea);
                }
                this.$toaster.success('Đã copy code');
            } catch (error) {
                this.$toaster.error('Không thể copy code');
            }
        }
    }
};
</script>

<style scoped>
.legend-icon-compact {
    display: flex;
    align-items: center;
    gap: 0.4rem;
}

.legend-icon-input {
    flex: 1;
}

::v-deep .legend-icon-input .custom-file-label,
::v-deep .legend-icon-input .custom-file-label::after {
    height: 30px;
    line-height: 1.35;
    font-size: 0.8rem;
}

.legend-icon-preview {
    width: 24px;
    height: 24px;
    object-fit: contain;
    border: 1px solid #d8d8d8;
    border-radius: 4px;
    background: #fff;
}

.legend-icon-remove {
    padding: 0.05rem 0.35rem;
}

.api-guide-code {
    background: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    padding: 12px;
    white-space: pre-wrap;
}
</style>
